import React, { useState, useEffect } from 'react';
import { reportsApi } from '../../services/api';

// Issue #410: German labels for report categories.
// Post and comment use the same categories; marketplace objects use a
// different set. The same category strings come back from the backend, so
// we resolve them with one shared lookup.
const CATEGORY_LABELS = {
  // Post / comment categories (see frontend/src/translations/index.js)
  hate_speech: 'Hate speech / Diskriminierung',
  spam: 'Spam / Werbung',
  personal_data: 'Veröffentlichung von persönlichen Daten',
  misinformation: 'Verbreitung von Misinformationen',
  violence: 'Bedrohung von Gewalt',
  sexual: 'Sexualisierte Inhalte',
  // Marketplace categories
  inappropriate_content: 'Unpassender Inhalt',
  threatening_content: 'Bedrohender Inhalt',
  commercial_seller: 'Mutmaßlich gewerblicher Anbieter',
  fraudulent_content: 'Betrügerischer Inhalt',
  counterfeit: 'Fälschung',
  academic_integrity: 'Verstoß gegen Community Guideline für akademische Integrität',
};

function formatCategory(category) {
  if (!category) return '-';
  return CATEGORY_LABELS[category] || category;
}

/**
 * Issue #410: Shared Meldungen section.
 *
 * Renders a table of report category + optional text for the given content.
 * The section is only rendered if at least one report exists.
 *
 * Props:
 *   - targetType: 'post' or 'comment'
 *   - targetId:   UUID of the post (or marketplace object) or comment
 */
function MeldungenSection({ targetType, targetId }) {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchReports = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = targetType === 'comment'
          ? await reportsApi.listContentReportsForComment(targetId)
          : await reportsApi.listContentReportsForPost(targetId);
        if (cancelled) return;
        setReports(response.data.reports || []);
      } catch (err) {
        if (cancelled) return;
        setError('Fehler beim Laden der Meldungen');
        console.error('MeldungenSection fetch error:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (targetId) {
      fetchReports();
    }
    return () => { cancelled = true; };
  }, [targetType, targetId]);

  if (isLoading) {
    return null;
  }

  if (error) {
    return (
      <section className="detail-section" data-testid="meldungen-section">
        <div className="section-header">
          <h2>Meldungen</h2>
        </div>
        <div className="error-message">{error}</div>
      </section>
    );
  }

  // Acceptance criterion: the section is only displayed when at least one
  // report exists. If there are none, render nothing.
  if (reports.length === 0) {
    return null;
  }

  return (
    <section className="detail-section" data-testid="meldungen-section">
      <div className="section-header">
        <h2>Meldungen</h2>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Kategorie</th>
              <th>Optionale Texte</th>
            </tr>
          </thead>
          <tbody>
            {reports.map((report) => (
              <tr key={report.id}>
                <td>{formatCategory(report.category)}</td>
                <td>{report.reason && report.reason.trim() !== '' ? report.reason : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default MeldungenSection;
